import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IWizardStepRendererProps, StackPanel, Typography, useWizardStep } from '@agilepoint/wat-react-ux';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { IMobileApplicationDetails } from 'modules/create-new-app/queries';
import { MobileAppHelpLink, StyledStackPanel } from 'common';
import { WizardNavigationActions } from '../footers';
import { BasicSettingsForm } from '../forms';

interface IBasicSettingsProps extends IWizardStepRendererProps {
    basicSettingsDetails: {
        companyInfoEmail: string;
        companyName: string;
        companySupportEmail: string;
        companyWebsite: string;
        copyrightInfo: string;
        mobileAppName?: string;
        mobileAppDescription?: string;
        mobileAppVersion?: string;
        defaultLocale?: string;
        eulaLink?: string;
        eulaText?: string;
    }
    onSaveBasicSettingsDetails: (payload: IBasicSettingsFormFields) => Promise<{ UpdateMobileAppResult: IMobileApplicationDetails }>
}

export interface IBasicSettingsFormFields {
    mobileAppName: string;
    mobileAppVersion: string;
    defaultLocale: string;
    mobileAppDescription: string;
    companyName: string;
    companyWebsite: string;
    companySupportEmail: string;
    companyInfoEmail: string;
    copyrightInfo: string;
    eulaLink: string;
    eulaText: string;
    eulaOptions: string;
}

export const BasicSettings = React.memo((props: IBasicSettingsProps) => {
    const { stepId, basicSettingsDetails, onSaveBasicSettingsDetails } = props;
    const { saveData, data } = useWizardStep(stepId);
    const translate = useTranslate();

    console.log('basicSettingsDetails', basicSettingsDetails);

    const form = useForm<IBasicSettingsFormFields>({
        mode: 'onBlur',
        defaultValues: {
            ...basicSettingsDetails,
            defaultLocale: basicSettingsDetails.defaultLocale || 'default',
            mobileAppName: basicSettingsDetails.mobileAppName ?? '',
            mobileAppVersion: basicSettingsDetails.mobileAppVersion ?? '1.0',
            eulaOptions: basicSettingsDetails.eulaText ? 'customEULA' : 'standardEULA',
            eulaLink: basicSettingsDetails.eulaLink ?? '',
            eulaText: basicSettingsDetails.eulaText ?? ''
        }
    });

    const { trigger, getValues, reset } = form;

    const onNext = React.useCallback(async () => {
        if (!await trigger()) {
            return false;
        }
        const formData = getValues();

        return onSaveBasicSettingsDetails(formData)
            .then((result) => {
                saveData({ updateMobileAppRsponse: result.UpdateMobileAppResult, formData });
                return true;
            })
            .catch(() => false);
    }, [trigger, getValues, onSaveBasicSettingsDetails, saveData]);

    React.useEffect(() => {
        data && reset(data.formData);
    }, [reset, data]);

    return (
        <FormProvider {...form}>
            <MobileAppHelpLink id="basicSettings" />
            <StyledStackPanel margin='30px 0px 0px 0px' gap="30" height="calc(100% - 50px)" orientation='vertical' alignment="center" width='100%'>
                <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                    <Typography.H2 title={translate('modules.createNewApp.steps.basicSettings.headerLabel')}>
                        <Localize translate='modules.createNewApp.steps.basicSettings.headerLabel' />
                    </Typography.H2>
                    <Typography.Medium title={translate('modules.createNewApp.steps.basicSettings.subHeaderLabel')}>
                        <Localize translate='modules.createNewApp.steps.basicSettings.subHeaderLabel' />
                    </Typography.Medium>
                </StackPanel>
                <StyledStackPanel width='100%' justifyContent={'center'} overflow='auto' height="calc(100% - 95px)">
                    <BasicSettingsForm />
                </StyledStackPanel>
            </StyledStackPanel>
            <WizardNavigationActions
                stepId={stepId}
                hook={onNext} />
        </FormProvider>
    );
});
